/**
 * WhatsApp Campaigns TanStack Query Hooks
 *
 * React Query hooks for WhatsApp broadcast campaign management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  waApi,
  type WACampaignsListParams,
  type WACampaignsListResponse,
  type WACampaignStats,
  type WACampaignDetail,
  type WACampaign,
  type WACampaignCreateInput,
  type WACampaignUpdateInput,
  type WAAudiencePreviewResponse,
  type WAAudienceFilter,
  type WACampaignMessagesParams,
  type WACampaignMessagesResponse,
} from '@/lib/api-client'

// Query keys
export const waCampaignsKeys = {
  all: ['wa-campaigns'] as const,
  lists: () => [...waCampaignsKeys.all, 'list'] as const,
  list: (params?: WACampaignsListParams) => [...waCampaignsKeys.lists(), params] as const,
  stats: () => [...waCampaignsKeys.all, 'stats'] as const,
  details: () => [...waCampaignsKeys.all, 'detail'] as const,
  detail: (id: number) => [...waCampaignsKeys.details(), id] as const,
  audiencePreview: (id: number) => [...waCampaignsKeys.all, 'audience', id] as const,
  filterPreview: (filter: WAAudienceFilter) => [...waCampaignsKeys.all, 'filter-preview', filter] as const,
  messages: (id: number, params?: WACampaignMessagesParams) => [...waCampaignsKeys.all, 'messages', id, params] as const,
}

/**
 * Hook to fetch list of WhatsApp campaigns
 */
export function useWACampaigns(params?: WACampaignsListParams) {
  return useQuery<WACampaignsListResponse>({
    queryKey: waCampaignsKeys.list(params),
    queryFn: () => waApi.campaigns.list(params),
    staleTime: 30 * 1000, // 30 seconds
  })
}

/**
 * Hook to fetch WhatsApp campaign statistics
 */
export function useWACampaignStats() {
  return useQuery<WACampaignStats>({
    queryKey: waCampaignsKeys.stats(),
    queryFn: () => waApi.campaigns.stats(),
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Hook to fetch single WhatsApp campaign details
 */
export function useWACampaign(id: number | null) {
  return useQuery<WACampaignDetail>({
    queryKey: waCampaignsKeys.detail(id ?? 0),
    queryFn: () => waApi.campaigns.get(id!),
    enabled: !!id,
  })
}

/**
 * Hook to create a new campaign
 */
export function useCreateWACampaign() {
  const queryClient = useQueryClient()

  return useMutation<WACampaign, Error, WACampaignCreateInput>({
    mutationFn: (data) => waApi.campaigns.create(data),
    onSuccess: () => {
      // Invalidate all campaign queries after creation
      queryClient.invalidateQueries({ queryKey: waCampaignsKeys.all })
    },
  })
}

/**
 * Hook to update a campaign
 */
export function useUpdateWACampaign() {
  const queryClient = useQueryClient()

  return useMutation<WACampaign, Error, { id: number; data: WACampaignUpdateInput }>({
    mutationFn: ({ id, data }) => waApi.campaigns.update(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate specific campaign and list
      queryClient.invalidateQueries({ queryKey: waCampaignsKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: waCampaignsKeys.lists() })
    },
  })
}

/**
 * Hook to delete a campaign
 */
export function useDeleteWACampaign() {
  const queryClient = useQueryClient()

  return useMutation<{ success: boolean; message: string; deletedId: number }, Error, number>({
    mutationFn: (id) => waApi.campaigns.delete(id),
    onSuccess: () => {
      // Invalidate all campaign queries after delete
      queryClient.invalidateQueries({ queryKey: waCampaignsKeys.all })
    },
  })
}

/**
 * Hook to preview campaign audience
 */
export function useWACampaignAudiencePreview(id: number | null) {
  return useQuery<WAAudiencePreviewResponse>({
    queryKey: waCampaignsKeys.audiencePreview(id ?? 0),
    queryFn: () => waApi.campaigns.previewAudience(id!),
    enabled: !!id,
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Hook to preview audience with custom filter (mutation for on-demand calls)
 */
export function usePreviewAudienceFilter() {
  return useMutation<WAAudiencePreviewResponse, Error, WAAudienceFilter>({
    mutationFn: (filter) => waApi.campaigns.previewFilter(filter),
  })
}

/**
 * Hook to execute a campaign (immediately or scheduled)
 */
export function useExecuteWACampaign() {
  const queryClient = useQueryClient()

  return useMutation<
    { success: boolean; message: string; campaignId: number; scheduledAt?: string },
    Error,
    { id: number; scheduledAt?: string }
  >({
    mutationFn: ({ id, scheduledAt }) => waApi.campaigns.execute(id, scheduledAt ? { scheduledAt } : undefined),
    onSuccess: (_, { id }) => {
      // Invalidate campaign queries after execution
      queryClient.invalidateQueries({ queryKey: waCampaignsKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: waCampaignsKeys.lists() })
      queryClient.invalidateQueries({ queryKey: waCampaignsKeys.stats() })
    },
  })
}

/**
 * Hook to cancel a running campaign
 */
export function useCancelWACampaign() {
  const queryClient = useQueryClient()

  return useMutation<{ success: boolean; message: string; campaignId: number }, Error, number>({
    mutationFn: (id) => waApi.campaigns.cancel(id),
    onSuccess: (_, id) => {
      // Invalidate campaign queries after cancellation
      queryClient.invalidateQueries({ queryKey: waCampaignsKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: waCampaignsKeys.lists() })
      queryClient.invalidateQueries({ queryKey: waCampaignsKeys.stats() })
    },
  })
}

/**
 * Hook to fetch campaign messages (per-recipient results)
 */
export function useWACampaignMessages(id: number | null, params?: WACampaignMessagesParams) {
  return useQuery<WACampaignMessagesResponse>({
    queryKey: waCampaignsKeys.messages(id ?? 0, params),
    queryFn: () => waApi.campaigns.messages(id!, params),
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds
  })
}

/**
 * Hook to upload media for a campaign
 */
export function useUploadCampaignMedia() {
  const queryClient = useQueryClient()

  return useMutation<
    { success: boolean; mediaId: string; fileName: string; mimeType: string; size: number },
    Error,
    { id: number; file: File }
  >({
    mutationFn: ({ id, file }) => waApi.campaigns.uploadMedia(id, file),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: waCampaignsKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: waCampaignsKeys.lists() })
    },
  })
}

/**
 * Hook to delete media from a campaign
 */
export function useDeleteCampaignMedia() {
  const queryClient = useQueryClient()

  return useMutation<{ success: boolean; message: string }, Error, number>({
    mutationFn: (id) => waApi.campaigns.deleteMedia(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: waCampaignsKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: waCampaignsKeys.lists() })
    },
  })
}

/**
 * Get export URL for campaign results
 */
export function getWACampaignExportUrl(id: number): string {
  return waApi.campaigns.getExportUrl(id)
}
